import { useEffect, useState } from 'react';

import type { AiAssistantSource } from './types';

type UseOpenedSourceOptions = {
  messageId?: string;
  openDocumentFile: string | null;
  sources: readonly AiAssistantSource[] | undefined;
  /** Citation jumped to from chat; keep the matching card marked as opened. */
  targetSource?: number | null;
};

/** Tracks which source card stays marked as the most recently opened. */
export function useOpenedSource({
  messageId,
  openDocumentFile,
  sources,
  targetSource,
}: UseOpenedSourceOptions) {
  const [openedSource, setOpenedSource] = useState<number | null>(null);

  useEffect(() => {
    setOpenedSource(null);
  }, [messageId]);

  // Clear document marks when the preview closes. Must run before the
  // targetSource sync below so a chip jump in the same commit still wins.
  useEffect(() => {
    if (openDocumentFile != null) {
      return;
    }
    setOpenedSource((current) => {
      if (current == null) {
        return current;
      }
      const marked = sources?.find(
        (source, index) => (source.citationNumber ?? index + 1) === current
      );
      return marked?.kind === 'document' ? null : current;
    });
  }, [openDocumentFile, sources]);

  useEffect(() => {
    if (targetSource != null) {
      setOpenedSource(targetSource);
    }
  }, [targetSource]);

  return {
    markOpenedSource: setOpenedSource,
    openedSource,
  };
}
